import { useState } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { Search, Store } from 'lucide-react';
import { Sheet, SheetBody, SheetContent } from './ui/Sheet';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Alert } from './ui/Alert';
import { SkeletonRows } from './ui/Skeleton';
import { apiFetch } from '../lib/api';
import { formatMoney } from '../lib/format';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { ApiError } from '../types/api';
import type { ShopProduct, ShopProductImageResponse, ShopProductsResponse } from '../types/api';

/** What a picked product fills into a product block (the block's own fields, editable afterwards). */
export interface PickedProduct {
  name: string;
  price: string;
  imageSrc: string;
  href: string;
}

/** Same limit as the block schema (`ProductBlockSchema`). */
const NAME_MAX = 200;

const STOCK_LABELS: Record<ShopProduct['stockStatus'], string> = {
  instock: 'In stock',
  outofstock: 'Out of stock',
  onbackorder: 'On backorder',
};

/** The block's price text: the current price in the shop's currency, "From …" for a product with variations. */
export function productPriceText(product: ShopProduct): string {
  const amount = Number(product.price);
  if (product.price === '' || !Number.isFinite(amount)) return '';
  const text = formatMoney(amount, product.currency, 2);
  return product.variationsCount > 0 ? `From ${text}` : text;
}

export function pickedFields(product: ShopProduct, imageSrc: string): PickedProduct {
  return {
    name: product.name.slice(0, NAME_MAX),
    price: productPriceText(product),
    imageSrc,
    href: product.permalink,
  };
}

function listErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'Your role cannot choose products. Ask an editor or an admin.';
    if (err.body.message) return err.body.message;
  }
  return 'Could not load products from the shop. Try again.';
}

function ProductResults({
  onPick,
  busyId,
}: {
  onPick: (p: ShopProduct) => void;
  busyId: string | null;
}) {
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 300);

  const list = useInfiniteQuery({
    queryKey: ['woo-products', query],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      apiFetch<ShopProductsResponse>(
        `/woo/products?page=${pageParam}${query ? `&search=${encodeURIComponent(query)}` : ''}`,
      ),
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    staleTime: 30_000,
  });
  const products = list.data?.pages.flatMap((p) => p.products) ?? [];

  return (
    <SheetBody>
      <div className="relative">
        <Search
          size={15}
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
        />
        <Input
          aria-label="Search products"
          placeholder="Search products"
          className="pl-9"
          maxLength={100}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {list.isError ? <Alert>{listErrorText(list.error)}</Alert> : null}
      {list.isPending ? <SkeletonRows label="Loading…" rows={4} rowClassName="h-[74px]" /> : null}
      {list.isSuccess && products.length === 0 ? (
        <p className="text-sm text-muted m-0">
          {query ? `No products match "${query}".` : 'The shop has no products to show.'}
        </p>
      ) : null}

      <ul className="flex flex-col gap-2 list-none m-0 p-0">
        {products.map((product) => (
          <li key={product.id}>
            <button
              type="button"
              aria-label={`Use ${product.name}`}
              disabled={busyId !== null}
              className="w-full flex items-center gap-3 text-left rounded-xl border border-border bg-white p-2 hover:border-accent focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-60"
              onClick={() => onPick(product)}
            >
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="h-14 w-14 flex-shrink-0 rounded-md bg-bg object-contain"
                />
              ) : (
                <span className="h-14 w-14 flex-shrink-0 rounded-md bg-bg" aria-hidden="true" />
              )}
              <span className="min-w-0 flex-grow">
                <span className="block text-sm font-medium truncate">{product.name}</span>
                <span className="block text-xs text-muted">
                  {productPriceText(product) || 'No price'}
                  {product.salePrice !== '' ? ' · On sale' : ''}
                  {' · '}
                  {STOCK_LABELS[product.stockStatus]}
                  {product.variationsCount > 0 ? ` · ${product.variationsCount} variations` : ''}
                </span>
              </span>
              {busyId === product.id ? (
                <span className="text-xs text-muted flex-shrink-0">Copying image…</span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>

      {list.hasNextPage ? (
        <Button
          variant="secondary"
          size="sm"
          disabled={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          Show more
        </Button>
      ) : null}
    </SheetBody>
  );
}

/**
 * "Choose from shop" for the product block: a button that opens a right-hand panel with the
 * connected WooCommerce shop's products (search, thumbnails). Picking one fills the block's
 * name, price, image and link through `onPick`; the fields stay editable. The image is copied
 * onto our own media domain first (`POST /api/woo/products/image`), so a mail never points
 * at the shop. If that copy fails the other fields are still filled, the image is left empty
 * and the note below the button says why. Campaign UTM tags are added to the link at send
 * time, the same as for a typed link.
 */
export function ShopProductPicker({ onPick }: { onPick: (fields: PickedProduct) => void }) {
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const pick = useMutation({
    mutationFn: async (product: ShopProduct): Promise<PickedProduct> => {
      if (!product.imageUrl) return pickedFields(product, '');
      try {
        const image = await apiFetch<ShopProductImageResponse>('/woo/products/image', {
          method: 'POST',
          body: { url: product.imageUrl },
        });
        if (image.status === 'copied') return pickedFields(product, image.url);
        setNotice(`The image could not be copied (${image.message}). Upload one instead.`);
      } catch {
        setNotice('The image could not be copied. Upload one instead.');
      }
      return pickedFields(product, '');
    },
    onSuccess: (fields) => {
      onPick(fields);
      setOpen(false);
    },
  });

  return (
    <div className="flex flex-col gap-1.5">
      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        onClick={() => {
          setNotice(null);
          setOpen(true);
        }}
      >
        <Store size={15} aria-hidden="true" />
        Choose from shop
      </Button>
      {notice ? (
        <p role="status" className="text-xs text-muted m-0">
          {notice}
        </p>
      ) : null}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          eyebrow="Shop"
          title="Choose a product"
          description="Pick a product from your shop to fill in the name, price, image and link. You can still edit them afterwards."
        >
          <ProductResults
            busyId={pick.isPending ? pick.variables.id : null}
            onPick={(product) => pick.mutate(product)}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
